import { useEffect, useRef } from "react";

/**
 * Custom hook to manage cell selection behavior across AG Grid tables.
 *
 * Features:
 * - Click a cell once to select it
 * - Click the same cell again to deselect it
 * - Click a different table's cell to clear previous selection
 * - Click outside any grid to clear all selections
 */
export const useCellSelectionManager = () => {
  // Track last clicked cell to enable click-to-deselect behavior
  const lastClickedCellRef = useRef<{
    rowIndex: number;
    columnId: string;
    gridApi: any;
  } | null>(null);

  // Clear selections when clicking outside any grid
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const isInsideGrid = target.closest(".ag-root");

      if (!isInsideGrid && lastClickedCellRef.current) {
        // Clear the selection in the last clicked grid
        lastClickedCellRef.current.gridApi.clearRangeSelection();
        lastClickedCellRef.current.gridApi.clearFocusedCell();
        lastClickedCellRef.current = null;
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // When allowing multi Cell Selection - Need a method of clicking again to deselect the cell.
  const onCellMouseDown = (params: any) => {
    const currentCell = {
      rowIndex: params.rowIndex,
      columnId: params.column.getId(),
      gridApi: params.api,
    };

    const lastCell = lastClickedCellRef.current;

    // If there was a previous selection in a different grid, clear it
    if (lastCell && lastCell.gridApi !== params.api) {
      lastCell.gridApi.clearRangeSelection();
      lastCell.gridApi.clearFocusedCell();
    }

    // Check if clicking the same cell twice in the same grid
    if (
      lastCell &&
      lastCell.gridApi === params.api &&
      lastCell.rowIndex === currentCell.rowIndex &&
      lastCell.columnId === currentCell.columnId
    ) {
      // Check if this cell is editable - if so, allow double-click to enter edit mode
      const isEditable = params.colDef?.editable;
      const editableResult = typeof isEditable === 'function' ? isEditable(params) : isEditable;

      if (!editableResult) {
        // Only deselect non-editable cells to avoid interfering with double-click edit
        params.event.preventDefault();
        params.api.clearRangeSelection();
        params.api.clearFocusedCell();
        lastClickedCellRef.current = null;
      }
      // For editable cells, don't prevent default - allow AG Grid to handle double-click edit
    } else {
      // Different cell or different grid - update the last clicked cell
      lastClickedCellRef.current = currentCell;
    }
  };

  return { onCellMouseDown };
};
